<template>
    <b-modal :id="modalId" size="lg" hide-footer @shown="initializeMap" @hidden="resetMap">
        <template #modal-title>
            <h6 class="fw-bold mb-0 akkurate-green">{{ title }}</h6>
        </template>

        <div v-if="loading" class="col-12 text-center">
            <beat-loader-component :isLoading="loading" />
            <p class="mt-2">{{ message }}</p>
        </div>

        <div v-show="!loading" ref="mapContainer" style="height: 400px;"></div>

        <div v-if="routeInfo" class="mt-2 text-center akkurate-dark">
            <span class="info">Distance: {{ routeInfo.distance }}</span>
            <span class="success ml-2">ETA: {{ routeInfo.duration }}</span>
        </div>

        <div class="d-flex justify-content-end mt-3 mb-2">
            <b-button v-if="!trackingDirections" :disabled="loading" class="btn btn-size me-2 akkurate-green-bg" @click="startDirections">
                Start Directions
            </b-button>
            <b-button v-else :disabled="loading" class="btn btn-size akkurate-danger-bg me-2" @click="stopDirections">
                Stop Directions
            </b-button>

            <b-button v-if="!trackingLive" :disabled="loading" class="btn btn-size akkurate-blue-bg ml-2" @click="startLiveTracking">
                Start Tracking
            </b-button>
            <b-button v-else :disabled="loading" class="btn btn-size akkurate-danger-bg ml-2" @click="stopLiveTracking">
                Stop Tracking
            </b-button>
        </div>
    </b-modal>
</template>

<script>
import BeatLoaderComponent from "@/views/components/Loader/BeatLoaderComponent.vue";
import { loadGoogleMapsScript } from "@/utils/googleMapsLoader";

export default {
    name: "MapModal",
    components: { BeatLoaderComponent },
    props: {
        modalId: { type: String, default: "map-modal" },
        digitalAddress: { type: String, required: false },
        title: { type: String, default: "Location on Map" },
        coords: { type: Object, default: null },
        memberInfo: {
            type: Object, default: () => ({
                name: "Member",
                phone: "N/A",
                email: "N/A",
                status: "N/A",
            })
        }
    },
    data() {
        return {
            map: null,
            markers: [],
            loading: true,
            message: "Loading map, please wait...",
            activeInfoWindow: null,
            directionsService: null,
            directionsRenderer: null,
            memberLocation: null,
            userLocation: null,
            userMarker: null,

            // tracking states
            trackingDirections: false,
            trackingLive: false,
            liveDirectionsPolyline: null,

            // watch IDs
            watchIdDirections: null,
            watchIdLive: null,

            trackingInitialized: false,
            routeInfo: null,
            lastRouteUpdate: 0,
            fallbackLine: null,

            // Compass & rotation smoothing
            deviceOrientationHandler: null,
            currentHeading: 0,
            targetHeading: 0,
            rotationRafId: null,

            // Field-of-view polygon
            fovPolygon: null,
            fovConfig: { radius: 80, angleDeg: 60, points: 12 }, // radius in meters

            // store last position for bearing calculation
            lastUserPositionForBearing: null
        };
    },
    methods: {
        async initializeMap() {
            this.loading = true;
            this.message = "Loading map, please wait...";
            this.clearMarkers();

            try {
                await loadGoogleMapsScript();

                let location = null;
                if (this.digitalAddress && !this.digitalAddress.includes("Ghana")) {
                    try {
                        location = await this.geocodeAddress(this.digitalAddress);
                    } catch (err) {
                        console.warn("Geocoding failed:", err.message);
                    }
                }

                console.log(this.extractLatLng(this.digitalAddress))
                if (!location) location = this.extractLatLng(this.digitalAddress)

                if (!location && this.coords?.latitude != null) location = this.coords;

                if (!location) {
                    this.message = "No valid member location found.";
                    this.loading = true;
                    return;
                }

                this.memberLocation = location;

                navigator.geolocation.getCurrentPosition(
                    pos => {
                        console.log(pos)
                        this.userLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                        this.initMap(this.memberLocation);

                        this.addMarker(this.memberLocation, this.memberInfo.name, "red",
                            `<div><strong>${this.memberInfo.name}</strong><br>${this.memberInfo.phone}<br>${this.memberInfo.status}<br>${this.memberInfo.email}</div>`
                        );
                        // Create user marker with heading arrow
                        this.addOrUpdateUserMarker(this.userLocation, pos.coords.heading ?? null);

                        const bounds = new google.maps.LatLngBounds();
                        bounds.extend(this.memberLocation);
                        bounds.extend(this.userLocation);
                        this.map.fitBounds(bounds);

                        this.setupCompassAndSmoothing();

                        this.loading = false;
                    },
                    err => {
                        console.warn("User location failed:", err.message);
                        this.initMap(this.memberLocation);
                        this.addMarker(this.memberLocation, this.memberInfo.name, "red",
                            `<div><strong>${this.memberInfo.name}</strong><br>${this.memberInfo.phone}<br>${this.memberInfo.status}<br>${this.memberInfo.email}</div>`
                        );
                        this.loading = false;
                    },
                    { enableHighAccuracy: true }
                );
            } catch (error) {
                console.error("Map load error:", error);
                this.message = "Failed to load map.";
                this.loading = false;
            }
        },

        geocodeAddress(address) {
            return new Promise((resolve, reject) => {
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({ address }, (results, status) => {
                    if (status === "OK" && results[0]) {
                        resolve({
                            lat: results[0].geometry.location.lat(),
                            lng: results[0].geometry.location.lng()
                        });
                    } else {
                        reject(new Error(`Geocoding failed: ${status}`));
                    }
                });
            });
        },

        initMap(center) {
            this.map = new google.maps.Map(this.$refs.mapContainer, { center, zoom: 15 });
            this.directionsService = new google.maps.DirectionsService();
            this.directionsRenderer = new google.maps.DirectionsRenderer({ suppressMarkers: true });
            this.directionsRenderer.setMap(this.map);
            this.loading = false;
        },

        addMarker(location, title, color = "red", infoContent = null) {
            const marker = new google.maps.Marker({
                position: location,
                map: this.map,
                title,
                icon: `http://maps.google.com/mapfiles/ms/icons/${color}-dot.png`,
                animation: google.maps.Animation.DROP
            });

            if (infoContent) {
                const infoWindow = new google.maps.InfoWindow({ content: infoContent });
                marker.addListener("click", () => {
                    if (this.activeInfoWindow) this.activeInfoWindow.close();
                    infoWindow.open(this.map, marker);
                    this.activeInfoWindow = infoWindow;
                });
            }

            this.markers.push(marker);
            return marker;
        },

        // Add or update user marker using an arrow Symbol; animate rotation smoothly
        addOrUpdateUserMarker(location, heading = null) {
            // Compute bearing from last movement if heading not provided
            if ((heading === null || heading === undefined) && this.lastUserPositionForBearing) {
                const last = this.lastUserPositionForBearing;
                heading = this.computeBearing(last.lat, last.lng, location.lat, location.lng);
            }

            // set target heading
            this.targetHeading = heading !== null && heading !== undefined ? heading : this.targetHeading;

            // store last pos for next bearing computation
            this.lastUserPositionForBearing = { lat: location.lat, lng: location.lng };

            const svgIcon = this.buildArrowSvg(); // returns a Symbol-like object via path
            const icon = {
                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                scale: 6,
                strokeWeight: 1,
                strokeColor: "#0b5cff",
                fillColor: "#0b5cff",
                fillOpacity: 1,
                rotation: this.currentHeading || (this.targetHeading || 0)
            };

            if (this.userMarker) {
                // Move smoothly and update icon rotation target (actual rotation is handled by RAF loop)
                this.moveMarkerSmoothly(this.userMarker, location);
                // update icon (we don't forcibly reset rotation here because RAF smoothing handles it)
                const newIcon = Object.assign({}, icon);
                this.userMarker.setIcon(newIcon);
            } else {
                this.userMarker = new google.maps.Marker({
                    position: location,
                    map: this.map,
                    title: "You",
                    icon,
                    animation: google.maps.Animation.DROP,
                    zIndex: 999
                });

                const infoWindow = new google.maps.InfoWindow({
                    content: "<div><strong>You</strong><br>Your current location</div>"
                });
                this.userMarker.addListener("click", () => {
                    if (this.activeInfoWindow) this.activeInfoWindow.close();
                    infoWindow.open(this.map, this.userMarker);
                    this.activeInfoWindow = infoWindow;
                });
            }

            // Ensure smooth rotation loop is running
            if (!this.rotationRafId) this.rotationLoop();

            // Update or create Field-of-View cone
            this.updateFovCone(location, this.targetHeading);
        },

        // Build a small SVG path for an arrow if needed (kept for possible future use)
        buildArrowSvg() {
            // (Not used directly - kept for extension)
            return "M0 -10 L6 6 L0 2 L-6 6 Z";
        },

        // Compute bearing between two lat/lng in degrees
        computeBearing(lat1, lon1, lat2, lon2) {
            const toRad = deg => deg * Math.PI / 180;
            const toDeg = rad => rad * 180 / Math.PI;
            const dLon = toRad(lon2 - lon1);
            const a1 = toRad(lat1);
            const a2 = toRad(lat2);

            const y = Math.sin(dLon) * Math.cos(a2);
            const x = Math.cos(a1) * Math.sin(a2) - Math.sin(a1) * Math.cos(a2) * Math.cos(dLon);
            const brng = Math.atan2(y, x);
            return (toDeg(brng) + 360) % 360;
        },

        // Smooth rotation interpolation loop (uses requestAnimationFrame)
        rotationLoop() {
            const step = 6; // degrees per frame approx — controls smoothness
            const loop = () => {
                // approach targetHeading shortest way
                const current = ((this.currentHeading % 360) + 360) % 360;
                let target = ((this.targetHeading % 360) + 360) % 360;
                let diff = target - current;
                if (diff > 180) diff -= 360;
                if (diff < -180) diff += 360;

                if (Math.abs(diff) < 0.5) {
                    this.currentHeading = target;
                } else {
                    this.currentHeading = current + Math.sign(diff) * Math.min(Math.abs(diff), step * (1 / 60) * 10);
                }

                // update icon rotation if userMarker exists
                if (this.userMarker) {
                    const icon = this.userMarker.getIcon();
                    if (icon) {
                        const newIcon = Object.assign({}, icon);
                        newIcon.rotation = this.currentHeading;
                        this.userMarker.setIcon(newIcon);
                    }
                    // keep updating FOV as rotation changes
                    if (this.userLocation) this.updateFovCone(this.userLocation, this.currentHeading);
                }

                // request next frame if not at target
                const atTarget = Math.abs(diff) < 0.5;
                if (!atTarget) {
                    this.rotationRafId = requestAnimationFrame(loop);
                } else {
                    // stop RAF to save cycles; clear id
                    cancelAnimationFrame(this.rotationRafId || 0);
                    this.rotationRafId = null;
                }
            };

            // start loop
            if (!this.rotationRafId) this.rotationRafId = requestAnimationFrame(loop);
        },

        // Compass & deviceorientation setup (also uses movement bearing fallback)
        setupCompassAndSmoothing() {
            // deviceorientation absolute event if available
            const handler = (event) => {
                // Prefer absolute alpha if available, fallback to alpha
                const alpha = event.absolute ? event.alpha : event.alpha;
                if (alpha === null || alpha === undefined) return;

                // Convert alpha to compass heading (alpha is rotation around z-axis)
                // For many devices, alpha is degrees from north when absolute true
                // We normalize to 0..360
                let heading = alpha;
                if (typeof heading === "number") {
                    heading = (360 - heading + 90) % 360; // adjust depending on device orientation conventions
                    this.targetHeading = heading;
                    // ensure rotation loop running
                    if (!this.rotationRafId) this.rotationLoop();
                }
            };

            // store and attach listener
            this.deviceOrientationHandler = handler;
            if (window.DeviceOrientationEvent) {
                try {
                    window.addEventListener("deviceorientationabsolute", handler, true);
                    // also listen to fallback event name
                    window.addEventListener("deviceorientation", handler, true);
                } catch (e) {
                    console.warn("deviceorientation addEventListener failed", e);
                }
            }
        },

        // Draw/update a sector polygon for Field-of-View (FOV) in front of the arrow
        updateFovCone(center, heading) {
            if (!this.map || !center) return;

            const { radius, angleDeg, points } = this.fovConfig;
            const earthRadius = 6371000; // meters
            const toRad = d => d * Math.PI / 180;
            const toDeg = r => r * 180 / Math.PI;

            // Convert center lat/lng to numbers
            const lat0 = center.lat;
            const lng0 = center.lng;

            // Generate arc points for the sector
            const half = angleDeg / 2;
            const startAngle = heading - half;
            const endAngle = heading + half;
            const step = angleDeg / Math.max(points - 1, 1);

            const path = [];
            // include center as first point
            path.push({ lat: lat0, lng: lng0 });

            for (let a = startAngle; a <= endAngle + 0.0001; a += step) {
                const bearing = toRad(a);
                // destination point formula
                const dByR = radius / earthRadius;
                const lat = Math.asin(Math.sin(toRad(lat0)) * Math.cos(dByR) + Math.cos(toRad(lat0)) * Math.sin(dByR) * Math.cos(bearing));
                const lng = toRad(lng0) + Math.atan2(Math.sin(bearing) * Math.sin(dByR) * Math.cos(toRad(lat0)), Math.cos(dByR) - Math.sin(toRad(lat0)) * Math.sin(lat));
                path.push({ lat: toDeg(lat), lng: (toDeg(lng) + 540) % 360 - 180 }); // normalize lng
            }

            // close back to center
            path.push({ lat: lat0, lng: lng0 });

            if (this.fovPolygon) {
                this.fovPolygon.setPath(path);
            } else {
                this.fovPolygon = new google.maps.Polygon({
                    paths: path,
                    strokeColor: "#0b5cff",
                    strokeOpacity: 0.25,
                    strokeWeight: 1,
                    fillColor: "#0b5cff",
                    fillOpacity: 0.12,
                    map: this.map,
                    clickable: false,
                    zIndex: 1
                });
            }
        },

        clearMarkers() {
            this.markers.forEach(marker => marker.setMap(null));
            this.markers = [];
            if (this.userMarker) {
                this.userMarker.setMap(null);
                this.userMarker = null;
            }
            // remove fov polygon if present
            if (this.fovPolygon) {
                this.fovPolygon.setMap(null);
                this.fovPolygon = null;
            }
        },

        // 🔹 Directions Tracking
        startDirections() {
            this.trackingDirections = true;
            this.trackingInitialized = false;
            this.lastRouteUpdate = 0;

            // stop live tracking if active
            if (this.trackingLive) this.stopLiveTracking();

            this.watchIdDirections = navigator.geolocation.watchPosition(
                pos => {
                    const newLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                    this.userLocation = newLoc;
                    // set target heading from device heading if available else compute bearing
                    const heading = pos.coords.heading !== null && pos.coords.heading !== undefined ? pos.coords.heading
                        : (this.lastUserPositionForBearing ? this.computeBearing(this.lastUserPositionForBearing.lat, this.lastUserPositionForBearing.lng, newLoc.lat, newLoc.lng) : null);

                    this.addOrUpdateUserMarker(newLoc, heading);

                    const now = Date.now();
                    if (now - this.lastRouteUpdate > 5000) { // update every 5s
                        this.showDirections(this.userLocation, this.memberLocation);
                        this.lastRouteUpdate = now;
                    }
                },
                (err) => { console.warn("Directions watch error:", err); this.message = "Unable to get your location."; },
                { enableHighAccuracy: true, maximumAge: 5000, timeout: 8000 }
            );
        },
        stopDirections() {
            if (this.watchIdDirections) {
                navigator.geolocation.clearWatch(this.watchIdDirections);
                this.watchIdDirections = null;
            }
            this.trackingDirections = false;
            this.routeInfo = null;
            if (this.directionsRenderer) this.directionsRenderer.set("directions", null);
            // remove fallback line
            if (this.fallbackLine) {
                this.fallbackLine.setMap(null);
                this.fallbackLine = null;
            }
        },

        // 🔹 Live Tracking (real-time marker only)
        startLiveTracking() {
            this.trackingLive = true;
            // stop directions tracking if active
            if (this.trackingDirections) this.stopDirections();

            this.watchIdLive = navigator.geolocation.watchPosition(
                pos => {
                    const newLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                    this.userLocation = newLoc;

                    const heading = pos.coords.heading ??
                        (this.lastUserPositionForBearing ?
                            this.computeBearing(this.lastUserPositionForBearing.lat, this.lastUserPositionForBearing.lng, newLoc.lat, newLoc.lng)
                            : null
                        );

                    this.addOrUpdateUserMarker(newLoc, heading);

                    // Auto-center map
                    if (this.map) {
                        this.map.setCenter(newLoc);
                        this.map.setZoom(17);
                    }

                    // Show live directions
                    this.showLiveDirections(newLoc, this.memberLocation);

                },
                err => {
                    console.warn("Live tracking watch error:", err);
                    this.message = "Unable to get your location.";
                },
                { enableHighAccuracy: true, maximumAge: 50, timeout: 70 }
            );
        },
        stopLiveTracking() {
            if (this.watchIdLive) {
                navigator.geolocation.clearWatch(this.watchIdLive);
                this.watchIdLive = null;
            }
            this.trackingLive = false;

            // Remove live directions polyline
            if (this.liveDirectionsPolyline) {
                this.liveDirectionsPolyline.setMap(null);
                this.liveDirectionsPolyline = null;
            }
        },

        showDirections(userLocation, memberLocation) {
            if (!this.directionsService || !this.directionsRenderer) return;
            const request = { origin: userLocation, destination: memberLocation, travelMode: google.maps.TravelMode.DRIVING };

            this.directionsService.route(request, (result, status) => {
                if (status === "OK") {
                    if (this.fallbackLine) this.fallbackLine.setMap(null);
                    this.directionsRenderer.setDirections(result);

                    if (!this.trackingInitialized) {
                        this.map.fitBounds(result.routes[0].bounds);
                        this.trackingInitialized = true;
                    }

                    const leg = result.routes[0].legs[0];
                    // this.routeInfo = { distance: leg.distance.text, duration: leg.duration.text };
                } else {
                    if (this.fallbackLine) this.fallbackLine.setMap(null);
                    this.fallbackLine = new google.maps.Polyline({
                        path: [userLocation, memberLocation],
                        geodesic: true,
                        strokeColor: "#4285F4",
                        strokeOpacity: 0.6,
                        strokeWeight: 4,
                        map: this.map
                    });
                    // this.routeInfo = { distance: "N/A", duration: "N/A" };
                }
            });
        },

        resetMap() {
            // cleanup geolocation watchers
            if (this.watchIdDirections) {
                navigator.geolocation.clearWatch(this.watchIdDirections);
                this.watchIdDirections = null;
            }
            if (this.watchIdLive) {
                navigator.geolocation.clearWatch(this.watchIdLive);
                this.watchIdLive = null;
            }

            // stop rotation RAF
            if (this.rotationRafId) {
                cancelAnimationFrame(this.rotationRafId);
                this.rotationRafId = null;
            }

            // remove deviceorientation listeners
            if (this.deviceOrientationHandler) {
                try {
                    window.removeEventListener("deviceorientationabsolute", this.deviceOrientationHandler, true);
                    window.removeEventListener("deviceorientation", this.deviceOrientationHandler, true);
                } catch (e) {
                    // ignore
                }
                this.deviceOrientationHandler = null;
            }

            // clear markers, polygons, renderer, map
            this.clearMarkers();
            if (this.directionsRenderer) this.directionsRenderer.set("directions", null);
            if (this.fallbackLine) {
                this.fallbackLine.setMap(null);
                this.fallbackLine = null;
            }

            this.map = null;
            this.activeInfoWindow = null;
            this.loading = true;
            this.message = "Loading map, please wait...";

            this.trackingDirections = false;
            this.trackingLive = false;
            this.routeInfo = null;
            this.trackingInitialized = false;
            this.lastRouteUpdate = 0;
            this.currentHeading = 0;
            this.targetHeading = 0;
            this.lastUserPositionForBearing = null;
        },

        // 🔹 Smoothly move marker (unchanged)
        moveMarkerSmoothly(marker, newPos) {
            const oldPos = marker.getPosition();
            const newLatLng = new google.maps.LatLng(newPos.lat, newPos.lng);

            let i = 0;
            const steps = 20;
            const deltaLat = (newPos.lat - oldPos.lat()) / steps;
            const deltaLng = (newPos.lng - oldPos.lng()) / steps;

            const interval = setInterval(() => {
                i++;
                marker.setPosition({
                    lat: oldPos.lat() + deltaLat * i,
                    lng: oldPos.lng() + deltaLng * i
                });
                if (i >= steps) clearInterval(interval);
            }, 50);
        },

        showLiveDirections(userLocation, memberLocation) {
            if (!this.map || !userLocation || !memberLocation) return;

            // Remove previous polyline
            if (this.liveDirectionsPolyline) {
                this.liveDirectionsPolyline.setMap(null);
            }

            const request = {
                origin: userLocation,
                destination: memberLocation,
                travelMode: google.maps.TravelMode.DRIVING,
            };

            this.directionsService.route(request, (result, status) => {
                if (status === "OK") {
                    const path = result.routes[0].overview_path;
                    const lineSymbol = {
                        path: "M 0,-1 0,1", // simple vertical dash
                        strokeOpacity: 1,
                        scale: 4,
                        strokeColor: "#FFAA00"
                    }

                    this.liveDirectionsPolyline = new google.maps.Polyline({
                        path,
                        geodesic: true,
                        strokeColor: "#4285F4", // different color
                        strokeOpacity: 0.6,
                        strokeWeight: 5,
                        map: this.map,
                        zIndex: 2,
                        icons: [{
                            icon: lineSymbol,
                            offset: "0",
                            repeat: "20px" // distance between dashes
                        }]
                    });

                    const leg = result.routes[0].legs[0];
                    this.routeInfo = { distance: leg.distance.text, duration: leg.duration.text };
                } else {
                    // fallback straight line if directions fail
                    this.liveDirectionsPolyline = new google.maps.Polyline({
                        path: [userLocation, memberLocation],
                        geodesic: true,
                        strokeColor: "#FFAA00",
                        strokeOpacity: 0.5,
                        strokeWeight: 4,
                        map: this.map,
                        zIndex: 2
                    });
                    this.routeInfo = { distance: "N/A", duration: "N/A" };
                }
            });
        },
    },
    beforeDestroy() {
        // ensure cleanup if component destroyed
        this.resetMap();
    }
};
</script>

<style scoped>
/* Provide a subtle transition for icons that support CSS transforms.
   Note: Google Maps Marker icons rendered as Symbols don't use DOM CSS transforms,
   but leaving this helps if you replace with an OverlayView or custom DOM marker later. */
.map-user-arrow {
    transition: transform 0.18s linear;
    transform-origin: center center;
}
</style>